import React, { useEffect, useMemo, useState } from 'react';
import { ICreateDocByTemplateDialogModel } from '@models/document/actionsExecution/ICreateDocByTemplateDialogModel';
import { DocumentExecutionService } from '@services/actions/DocumentExecutionService';
import { FilesService } from '@services/FilesService';
import Modal from '@srm/ui/modal';
import clsx from 'clsx';
import Button from '@srm/ui/button';
import ModalLoadingOverlay from '@srm/ui/modal-loading-overlay';
import Hint from '@srm/ui/hint';
import DataTable, { IDataTableColumn, parseColumnSizing } from '@srm/ui/data-table';
import { ValueType } from '@srm/ui/types/ValueType';
import { ResultDocumentActionType } from '@models/document/actionsExecution/ICreateDocByTemplateModel';
import { ICreateDocByTemplateDialogExecutorProps } from './ICreateDocByTemplateDialogExecutorProps';

export const CreateDocByTemplateDialogExecutorModal: React.FC<ICreateDocByTemplateDialogExecutorProps> = (
    props: ICreateDocByTemplateDialogExecutorProps,
) => {
    const [errorText, setErrorText] = useState<string>();
    // Подготовка (список шаблонов) и создание документов — разные состояния: подготовку можно
    // бросить закрытием окна, создание — нет.
    const [loading, setLoading] = useState<boolean>(true);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const isBusy = loading || isSubmitting;
    const [templatesModel, setTemplatesModel] = useState<ICreateDocByTemplateDialogModel>();
    const [templatesList, setTemplatesList] = useState<any[]>([]);
    const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
    const service = new DocumentExecutionService(props.docId);

    const close = () => props.completeHandler?.(false);

    useEffect(() => {
        // Закрытое окно ответ подготовки не применяет.
        let ignore = false;
        setLoading(true);
        service
            .getCreateDocByTemplateDialogModel(props.actionKey)
            .then((res) => {
                if (ignore) return;
                setTemplatesModel(res.data);
                setTemplatesList(res.data.tableValues);
            })
            .catch((error) => {
                if (!ignore) setErrorText(error);
            })
            .finally(() => {
                if (!ignore) setLoading(false);
            });
        return () => {
            ignore = true;
        };
    }, []);

    // Колонки приходят из конфигурации действия — состав и ширины задаёт она.
    const columns = useMemo<IDataTableColumn<Record<string, string>>[]>(
        () =>
            (templatesModel?.tableColumns ?? []).map((column) => ({
                field: column.dataField ?? '',
                header: column.title ?? '',
                dataType: ValueType.Text,
                allowSorting: false,
                ...parseColumnSizing(column.width),
            })),
        [templatesModel?.tableColumns],
    );

    const selectedTemplates = useMemo(
        () => templatesList.filter((row) => selectedKeys.includes(row.templateKey)),
        [templatesList, selectedKeys],
    );

    return (
        <Modal
            className={clsx('modal-dlg-container')}
            size={props.modalSize}
            onClose={close}
            hasUnsavedInput={() => selectedKeys.length > 0}
            closeDisabled={isSubmitting}
            header={props.title}
            footer={
                <div className="modal-dlg-buttons">
                    <Button
                        disabled={selectedKeys.length === 0 || isBusy}
                        size="sm"
                        aria-label={props.okButtonText}
                        onClick={() => {
                            setErrorText(undefined);

                            const selectedRows = selectedTemplates;
                            if (templatesModel) {
                                if (templatesModel.result === ResultDocumentActionType.Download) {
                                    if (selectedRows.length === 0) {
                                        props.completeHandler && props.completeHandler(false);
                                        return;
                                    }
                                    // Окно ждёт файл, как создание: закрыть нельзя, отказ — в окне, без дубля-тоста.
                                    setIsSubmitting(true);
                                    FilesService.downloadFile(
                                        `documentExecution/downloadDialogTemplateDoc/${props.docId}/${props.actionKey}/${selectedRows[0].templateKey}`,
                                        undefined,
                                        { notifyOnError: false },
                                    )
                                        .then(() => {
                                            props.completeHandler && props.completeHandler(false);
                                        })
                                        .catch((error) => setErrorText(error))
                                        .finally(() => {
                                            setIsSubmitting(false);
                                        });
                                } else {
                                    setIsSubmitting(true);
                                    service
                                        .createDocByTemplateDialog(
                                            props.actionKey,
                                            selectedRows.map((row) => row.templateKey),
                                        )
                                        .then(() => {
                                            props.completeHandler && props.completeHandler(true);
                                        })
                                        .catch((error) => setErrorText(error))
                                        .finally(() => {
                                            setIsSubmitting(false);
                                        });
                                }
                            } else {
                                setErrorText('Не удалось получить модель данных.');
                            }
                        }}
                    >
                        {props.okButtonText}
                    </Button>
                    <Button
                        variant="light"
                        size="sm"
                        aria-label={props.cancelButtonText}
                        onClick={close}
                        disabled={isSubmitting}
                    >
                        {props.cancelButtonText}
                    </Button>
                </div>
            }
        >
            <ModalLoadingOverlay visible={isBusy}>
                <div>
                    {errorText && <Hint role="alert" title={`Ошибка: ${errorText}`} tone="danger" maxWidth={'100%'} />}

                    <DataTable
                        columns={columns}
                        data={templatesList}
                        getRowId={(row) => row.templateKey}
                        loading={isBusy}
                        selectable
                        onSelectionChange={setSelectedKeys}
                        defaultPageSize={templatesModel?.pageSize}
                        emptyText="Шаблоны отсутствуют"
                    />
                </div>
            </ModalLoadingOverlay>
        </Modal>
    );
};
